import type {
  AuthUser,
  CheckIn,
  Category,
  Goal,
  NewGoalInput,
  Tint,
  UpdateGoalInput,
} from './types';

/** Thin fetch wrapper for /api/* (backed by Cloudflare Pages Functions + D1). */

/** Thrown by any api call that gets a 401 — caller can trigger re-auth. */
export class UnauthorizedError extends Error {
  constructor() {
    super('unauthorized');
    this.name = 'UnauthorizedError';
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    // Fire once so anyone holding on to an auth state can drop it.
    // Skip for /api/auth/* itself so a bad login doesn't force a re-mount.
    if (!path.startsWith('/api/auth/')) {
      try { window.dispatchEvent(new Event('cosy:unauth')); } catch { /* noop */ }
    }
    throw new UnauthorizedError();
  }
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()) as T;
}

async function extractError(res: Response): Promise<string> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) return `${res.status} ${body.error}`;
    } catch { /* fall through */ }
  }
  return `${res.status} ${res.statusText || 'request failed'}`;
}

export const api = {
  // ── auth ────────────────────────────────────────────────────────────
  me: () => req<{ user: AuthUser | null }>('/api/auth/me').then(r => r.user),

  signup: (email: string, password: string) =>
    req<{ user: AuthUser }>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }).then(r => r.user),

  login: (email: string, password: string) =>
    req<{ user: AuthUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }).then(r => r.user),

  logout: () => req<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  forgotPassword: (email: string) =>
    req<{ ok: true }>('/api/auth/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    req<{ ok: true }>('/api/auth/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  // ── goals ───────────────────────────────────────────────────────────
  listGoals: () => req<{ goals: Goal[] }>('/api/goals').then(r => r.goals),

  createGoal: (input: NewGoalInput) =>
    req<{ goal: Goal }>('/api/goals', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then(r => r.goal),

  updateGoal: (id: string, patch: UpdateGoalInput) =>
    req<{ goal: Goal }>(`/api/goals/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }).then(r => r.goal),

  deleteGoal: (id: string) =>
    req<{ ok: true }>(`/api/goals/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ── check-ins ───────────────────────────────────────────────────────
  listCheckIns: (range?: { from?: string; to?: string }) => {
    const p = new URLSearchParams();
    if (range?.from) p.set('from', range.from);
    if (range?.to) p.set('to', range.to);
    const qs = p.toString();
    return req<{ checkIns: CheckIn[] }>(`/api/check-ins${qs ? `?${qs}` : ''}`).then(
      r => r.checkIns,
    );
  },

  addCheckIn: (goalId: string, day: string) =>
    req<{ checkIn: CheckIn }>('/api/check-ins', {
      method: 'POST',
      body: JSON.stringify({ goalId, day }),
    }).then(r => r.checkIn),

  removeCheckIn: (goalId: string, day: string) =>
    req<{ ok: true }>(
      `/api/check-ins?goalId=${encodeURIComponent(goalId)}&day=${encodeURIComponent(day)}`,
      { method: 'DELETE' },
    ),

  // ── categories ──────────────────────────────────────────────────────
  listCategories: () =>
    req<{ categories: Category[] }>('/api/categories').then(r => r.categories),

  createCategory: (input: { label: string; tint: Tint; icon?: string | null }) =>
    req<{ category: Category }>('/api/categories', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then(r => r.category),

  deleteCategory: (id: string, reassignTo?: string) => {
    const qs = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : '';
    return req<{ ok: true; reassigned: number }>(
      `/api/categories/${encodeURIComponent(id)}${qs}`,
      { method: 'DELETE' },
    );
  },
};
